/**
 * DataUploadPanel — upload CSV/XLSX to an INPUT_DATA node.
 *
 * Lives as a tab on NodeInfoBar, only shown when the selected node's
 * type is INPUT_DATA. On success, the backend validates the upload
 * against the node's declared TableSchema and promotes it to the
 * canonical artifact that downstream nodes read via ``bark.load_artifact``.
 *
 * UX:
 * - Drag-and-drop or click-to-pick.
 * - Inline validation report after upload: shows row/column counts,
 *   warnings (coercions), errors (structured per-column diffs).
 * - File size cap is surfaced up-front so the user knows the limit.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Upload, Loader2, CheckCircle2, AlertTriangle, XCircle, FileSpreadsheet } from 'lucide-react';
import type { NodeDefinition } from '../../types/modeling';
import api, { uploadWorkspaceFile } from '../../apiService';
import { useProcessViewerStore } from '../../stores/processViewerStore';
import { formatNumber } from '../../utils/plural';

interface UploadValidation {
    artifact_name: string;
    node_id: string;
    row_count: number;
    column_count: number;
    passed: boolean;
    errors: string[];
    warnings: string[];
    at: string;
}

interface ArtifactResult {
    artifact_name: string;
    artifact_path: string;
    validation: UploadValidation;
}

interface UploadSuccess {
    upload_id: string;
    summary?: string;
    artifact_name: string;
    validation: UploadValidation;
    paths: { raw: string; artifact: string };
    // Multi-output INPUT_DATA nodes (one upload, several declared
    // artifacts split by a discriminator column) get one entry per
    // output here. For a single-output node this is a one-item list
    // mirroring the primary fields above.
    artifact_results?: ArtifactResult[];
    limits: { max_bytes: number };
}

interface UploadFailure {
    error: string;
    validation?: UploadValidation;
}

interface UploadRecord {
    upload_id: string;
    filename: string;
    row_count?: number;
    column_count?: number;
    uploaded_at?: string;
    content_hash?: string;
}

interface DataUploadPanelProps {
    projectId: string;
    modelId: string;
    node: NodeDefinition;
    authToken: string;
    /** Logged-in user id — required for the report-library upload step
     *  (`uploadWorkspaceFile`), which scopes the upload to the workspace. */
    userId?: string;
}

const ACCEPTED = '.csv,.xlsx,.xls';
// BAR-308: the real server cap is the WOS `data_upload_max_bytes` (256 MiB
// default; env-overridable via DATA_UPLOAD_MAX_BYTES). Show it up-front so the
// panel no longer advertises (or rejects at) a stale 100 MiB before the first
// upload. The exact, env-aware value is reconciled from the import response's
// `limits.max_bytes` once a file lands.
const MAX_BYTES_DEFAULT = 256 * 1024 * 1024;

export default function DataUploadPanel({ projectId, modelId, node, authToken, userId }: DataUploadPanelProps) {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [dragOver, setDragOver] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [selectingUploadId, setSelectingUploadId] = useState<string | null>(null);
    const [result, setResult] = useState<UploadSuccess | null>(null);
    const [error, setError] = useState<UploadFailure | null>(null);
    // BAR-308: fetch the enforced server cap up-front so the panel never shows a
    // stale guess. The reconciled-from-response value (result.limits) wins once a
    // file lands; otherwise the fetched server value; otherwise the default.
    const [serverMaxBytes, setServerMaxBytes] = useState<number | null>(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const cfg = await api.getDataUploadConfig(projectId, authToken);
                if (!cancelled && cfg?.max_bytes) setServerMaxBytes(cfg.max_bytes);
            } catch {
                /* non-fatal: keep MAX_BYTES_DEFAULT */
            }
        })();
        return () => { cancelled = true; };
        // The cap is per-project and constant; intentionally NOT re-fetched on
        // authToken rotation (~hourly). authToken is captured at mount / project
        // change, which is always a valid token.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId]);

    const maxBytes = result?.limits.max_bytes ?? serverMaxBytes ?? MAX_BYTES_DEFAULT;
    const nodeMetadata = (node.metadata || {}) as any;
    const activeUploadId = typeof nodeMetadata.active_upload_id === 'string'
        ? nodeMetadata.active_upload_id
        : null;
    const uploadHistory: UploadRecord[] = Array.isArray(nodeMetadata.uploads)
        ? nodeMetadata.uploads.slice().reverse()
        : [];

    // Optimistically reflect the upload/select in the in-memory model. We do NOT
    // re-fetch via loadModel: the upload persists to the report-library model
    // store, but loadModel reads the workspace-event-log model (which the upload
    // doesn't mirror), so a re-fetch would WIPE the just-uploaded file back out
    // of the panel. Patching the store directly keeps the history list + active
    // marker + node badge correct for the session.

    const handleUpload = useCallback(async (file: File) => {
        setIsUploading(true);
        setResult(null);
        setError(null);
        try {
            if (!authToken) throw new Error('Not authenticated');
            // userId is optional through the NodeInfoBar/ProcessViewer chain, but
            // the report-library upload step needs it — fail with a clear message
            // rather than a confusing one if a caller didn't thread it through.
            if (!userId) throw new Error('Cannot upload: no user id in context.');

            // Client-side size guard so we fail fast before streaming;
            // the server still enforces its own cap.
            if (file.size > maxBytes) {
                setError({ error: `File exceeds ${Math.round(maxBytes / (1024 * 1024))} MiB limit` });
                return;
            }

            // BAR-308: two-step upload. The legacy single multipart POST to WOS
            // (api.uploadNodeData) was capped at Cloud Run's ~32 MiB buffered-
            // request ceiling, so large data files failed. Instead:
            //   A) land the bytes in report-library via the chat signed-URL flow
            //      (uploadWorkspaceFile routes files over the multipart threshold
            //      direct-to-GCS, bypassing the 32 MiB ceiling), then
            //   B) hand WOS the upload_id so it pulls + validates + promotes the
            //      artifact, returning the SAME body uploadNodeData used to.
            const workspaceId = `ws_${projectId}`;
            const up = await uploadWorkspaceFile(workspaceId, userId, projectId, file);
            if (!up.success || !up.uploaded_file?.upload_id) {
                throw new Error(up.error || 'Upload failed');
            }

            const data = await api.importNodeData(
                projectId,
                modelId,
                node.node_id,
                up.uploaded_file.upload_id,
                authToken,
            );
            setResult(data);
            useProcessViewerStore.getState().applyNodeUpload(
                modelId,
                node.node_id,
                (data as any).upload ?? null,
                (data as any).active_upload_id ?? data.upload_id,
            );
        } catch (e: any) {
            // FastAPI HTTPException returns {detail: string|object}. When
            // the backend raises UploadError with a validation payload,
            // detail is an object that we render verbatim.
            const detail = e?.detail ?? e?.response?.data?.detail;
            if (detail && typeof detail === 'object') {
                setError({ error: detail.error || 'Upload failed', validation: detail.validation });
            } else {
                setError({ error: String(detail || e?.message || e) });
            }
        } finally {
            setIsUploading(false);
        }
    }, [projectId, modelId, node.node_id, authToken, userId, maxBytes]);

    const handleSelectUpload = useCallback(async (uploadId: string) => {
        setSelectingUploadId(uploadId);
        setResult(null);
        setError(null);
        try {
            const data = await api.selectNodeInputUpload(
                projectId,
                modelId,
                node.node_id,
                { upload_id: uploadId },
                authToken,
            );
            setResult(data);
            useProcessViewerStore.getState().setNodeActiveUpload(
                modelId,
                node.node_id,
                (data as any).active_upload_id ?? uploadId,
            );
        } catch (e: any) {
            const detail = e?.detail ?? e?.response?.data?.detail;
            if (detail && typeof detail === 'object') {
                setError({ error: detail.error || 'Could not select upload', validation: detail.validation });
            } else {
                setError({ error: String(detail || e?.message || e) });
            }
        } finally {
            setSelectingUploadId(null);
        }
    }, [projectId, modelId, node.node_id, authToken]);

    const onDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files?.[0];
        if (file) handleUpload(file);
    }, [handleUpload]);

    const onPick = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) handleUpload(file);
        // Reset the input so the same file can be re-uploaded after a
        // failed validation without a page refresh.
        e.target.value = '';
    }, [handleUpload]);

    // Pull the declared schema columns so the drop zone can hint at
    // what shape of file the user should prepare. artifact_schema is
    // a discriminated union (table/scalar/matrix/time_series) — only
    // TABLE carries ``columns``. The outer ``artifact.artifact_type``
    // and the inner ``artifact.artifact_schema.artifact_type`` are
    // declared independently in the model (they should agree but TS
    // can't link them), so we narrow on the inner discriminator.
    const firstSchema = node.outputs[0]?.artifact?.artifact_schema;
    const declaredColumns =
        firstSchema?.artifact_type === 'table' ? firstSchema.columns : [];

    return (
        <div className="space-y-4">
            {/* Drop zone */}
            <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`
                    flex flex-col items-center justify-center
                    border-2 border-dashed rounded-lg p-6 text-center
                    cursor-pointer transition-colors
                    ${dragOver ? 'border-accent bg-accent-tint' : 'border-border-default bg-bg-warm/50 hover:bg-bg-warm'}
                    ${isUploading ? 'pointer-events-none opacity-60' : ''}
                `}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPTED}
                    onChange={onPick}
                    className="hidden"
                />
                {isUploading ? (
                    <>
                        <Loader2 className="w-8 h-8 text-accent animate-spin mb-2" strokeWidth={1.5} />
                        <p className="text-[13px] text-fg-secondary font-semibold">Uploading and validating…</p>
                    </>
                ) : (
                    <>
                        <Upload className="w-8 h-8 text-fg-tertiary mb-2" strokeWidth={1.5} />
                        <p className="text-[13px] text-fg font-semibold">Drop a CSV or XLSX here, or click to pick</p>
                        <p className="text-[11px] text-fg-tertiary mt-1">
                            Up to {Math.round(maxBytes / (1024 * 1024))} MiB · any standard CSV delimiter (, ; tab |) · validated against declared schema
                        </p>
                    </>
                )}
            </div>

            {/* Declared schema hint — helps the user prepare a valid file */}
            {declaredColumns.length > 0 && !result && !error && (
                <details className="group">
                    <summary className="cursor-pointer text-[11px] text-fg-tertiary hover:text-fg-secondary uppercase tracking-wider font-semibold">
                        Expected columns ({declaredColumns.length})
                    </summary>
                    <ul className="mt-2 space-y-1">
                        {declaredColumns.map((col: any) => (
                            <li key={col.name} className="flex items-center gap-2 text-[12px]">
                                <FileSpreadsheet className="w-3 h-3 text-fg-tertiary" strokeWidth={1.5} />
                                <span className="font-mono font-semibold text-fg">{col.name}</span>
                                <span className="font-mono text-[10px] text-fg-tertiary uppercase tracking-wider">
                                    {col.column_type}
                                </span>
                                {col.required && (
                                    <span className="font-mono text-[9px] uppercase tracking-wider text-danger bg-danger-bg px-1 py-px rounded">
                                        required
                                    </span>
                                )}
                            </li>
                        ))}
                    </ul>
                </details>
            )}

            {uploadHistory.length > 0 && (
                <div className="rounded-md border border-border-default bg-surface p-3 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                        <h4 className="text-[11px] text-fg-tertiary uppercase tracking-wider font-semibold">
                            Upload history
                        </h4>
                        {activeUploadId && (
                            <span className="font-mono text-[10px] text-fg-tertiary">
                                active: {activeUploadId}
                            </span>
                        )}
                    </div>
                    <ul className="space-y-1.5">
                        {uploadHistory.map((upload) => {
                            const isActive = upload.upload_id === activeUploadId;
                            const isSelecting = selectingUploadId === upload.upload_id;
                            return (
                                <li
                                    key={upload.upload_id}
                                    className={`flex items-center gap-2 rounded-md border px-2 py-1.5 ${
                                        isActive
                                            ? 'border-success bg-success-bg'
                                            : 'border-border-default bg-bg-warm/50'
                                    }`}
                                >
                                    <FileSpreadsheet className="w-3.5 h-3.5 text-fg-tertiary flex-shrink-0" strokeWidth={1.5} />
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-1.5 min-w-0">
                                            <span className="truncate text-[12px] font-semibold text-fg">
                                                {upload.filename || upload.upload_id}
                                            </span>
                                            {isActive && (
                                                <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-success uppercase tracking-wider flex-shrink-0">
                                                    <CheckCircle2 className="w-3 h-3" strokeWidth={1.5} />
                                                    Active
                                                </span>
                                            )}
                                        </div>
                                        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-fg-tertiary">
                                            <span>{formatNumber(upload.row_count ?? 0)} rows</span>
                                            <span>{upload.column_count ?? 0} columns</span>
                                            <span className="font-mono">{upload.upload_id}</span>
                                        </div>
                                    </div>
                                    {!isActive && (
                                        <button
                                            type="button"
                                            onClick={() => handleSelectUpload(upload.upload_id)}
                                            disabled={!!selectingUploadId}
                                            className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-[11px] font-semibold bg-surface text-fg-secondary border border-border-default hover:bg-bg-warm disabled:opacity-60 disabled:cursor-not-allowed"
                                        >
                                            {isSelecting ? (
                                                <Loader2 className="w-3 h-3 animate-spin" strokeWidth={1.5} />
                                            ) : (
                                                <CheckCircle2 className="w-3 h-3" strokeWidth={1.5} />
                                            )}
                                            Use this file
                                        </button>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}

            {/* Success state */}
            {result && (() => {
                // Multi-output node: one upload split by discriminator
                // into N typed artifacts. Render the per-artifact rows
                // explicitly — falling back to the primary `artifact_name`
                // alone hides 4 of the 5 partitions and makes it look
                // like the upload was single-table.
                const artifacts = result.artifact_results || [{
                    artifact_name: result.artifact_name,
                    artifact_path: result.paths.artifact,
                    validation: result.validation,
                }];
                const allWarnings = artifacts.flatMap(a => a.validation.warnings);
                return (
                    <div className="rounded-md border border-success bg-success-bg p-3 space-y-2">
                        <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-success" strokeWidth={1.5} />
                            <span className="text-[13px] font-semibold text-fg">
                                {artifacts.length > 1
                                    ? `Split into ${artifacts.length} artifacts`
                                    : <>Uploaded as <span className="font-mono">{artifacts[0].artifact_name}</span></>}
                            </span>
                        </div>
                        {artifacts.length > 1 ? (
                            <ul className="space-y-0.5 text-[12px] text-fg-secondary">
                                {artifacts.map(a => (
                                    <li key={a.artifact_name} className="flex items-center gap-2">
                                        <span className="font-mono">{a.artifact_name}</span>
                                        <span className="text-fg-tertiary">·</span>
                                        <span>{formatNumber(a.validation.row_count)} rows · {a.validation.column_count} columns</span>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="text-[12px] text-fg-secondary">
                                {formatNumber(artifacts[0].validation.row_count)} rows · {artifacts[0].validation.column_count} columns
                            </p>
                        )}
                        {allWarnings.length > 0 && (
                            <WarningList items={allWarnings} />
                        )}
                        <p className="text-[11px] text-fg-tertiary font-mono">
                            upload_id: {result.upload_id}
                        </p>
                    </div>
                );
            })()}

            {/* Error state */}
            {error && (
                <div className="rounded-md border border-danger bg-danger-bg p-3 space-y-2">
                    <div className="flex items-center gap-2">
                        <XCircle className="w-4 h-4 text-danger" strokeWidth={1.5} />
                        <span className="text-[13px] font-semibold text-fg">{error.error}</span>
                    </div>
                    {error.validation?.errors?.length ? (
                        <ul className="space-y-1 list-disc pl-5 text-[12px] text-fg-secondary">
                            {error.validation.errors.map((e, i) => <li key={i}>{e}</li>)}
                        </ul>
                    ) : null}
                    {error.validation?.warnings?.length ? (
                        <WarningList items={error.validation.warnings} />
                    ) : null}
                </div>
            )}
        </div>
    );
}

function WarningList({ items }: { items: string[] }) {
    return (
        <div>
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold text-warning mb-1">
                <AlertTriangle className="w-3 h-3" strokeWidth={1.5} />
                Warnings
            </div>
            <ul className="space-y-1 list-disc pl-5 text-[12px] text-fg-secondary">
                {items.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
        </div>
    );
}
